/**
 * agenticFieldHelpers.tsx — shared bits for the agentic tabs (E12-S3a/S3b):
 * the same input styling `SkillVersionEditForm.tsx` uses, plus one counter+
 * error field wrapper so every D1 `max_length` field (Persona, Boundaries,
 * Cadence, Facts, Scripts) gets the same "counter turns red past max,
 * `role=\"alert\"` inline error" treatment without repeating it.
 *
 * `expandable` (S3b) reuses `SkillVersionEditForm.tsx`'s `<dialog>`-based
 * expand-to-editor affordance (design doc §Step settings form) rather than
 * exporting/growing that file — same discard-on-cancel discipline: the
 * dialog edits a local buffer, "Salvar" commits it via `onChange`,
 * "Cancelar"/Esc discards it.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/Button';
import { inputClass } from '../../components/Field';

// Text actions (add/remove/edit): always underlined so they are not colour-only
// (WCAG 1.4.1), with a 24px minimum target (WCAG 2.5.8).
const TEXT_ACTION =
  'inline-flex min-h-6 items-center text-meta underline underline-offset-2 disabled:opacity-60';
export const textActionClass = `${TEXT_ACTION} text-gold`;
export const textActionDangerClass = `${TEXT_ACTION} text-status-down`;

export interface CounterFieldProps {
  id: string;
  label: string;
  value: string;
  maxLength: number;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: string;
  multiline?: boolean;
  rows?: number;
  hint?: string;
  /** Adds the `<dialog>`-based expand-to-editor button (S3b, long prose fields). */
  expandable?: boolean;
}

/** A single labelled input/textarea with a live `n/max` counter (red past max) and an inline `role="alert"` error. Client-side pre-check only — D4 rule 9 on the server is the authority. */
export const CounterField = ({
  id,
  label,
  value,
  maxLength,
  onChange,
  disabled = undefined,
  error = undefined,
  multiline = undefined,
  rows = 4,
  hint = undefined,
  expandable = undefined,
}: CounterFieldProps) => {
  const { t } = useTranslation();
  const overMax = value.length > maxLength;
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(value);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dialogTextareaRef = useRef<HTMLTextAreaElement>(null);

  const openDialog = () => {
    setDraft(value); // snapshot — the dialog edits this, not the live value
    setExpanded(true);
  };

  useEffect(() => {
    if (expanded && dialogRef.current != null && !dialogRef.current.open) {
      dialogRef.current.showModal();
      dialogTextareaRef.current?.focus();
    }
  }, [expanded]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-meta font-medium text-ink-2">
          {label}
        </label>
        <span
          data-testid={`${id}-counter`}
          className={`text-meta ${overMax ? 'text-status-down' : 'text-ink-3'}`}
        >
          {value.length}/{maxLength}
        </span>
      </div>
      {hint != null ? <p className="text-meta text-ink-3">{hint}</p> : null}
      {multiline ? (
        <textarea
          id={id}
          data-testid={id}
          rows={rows}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className={`${inputClass} w-full`}
        />
      ) : (
        <input
          id={id}
          data-testid={id}
          type="text"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className={`${inputClass} w-full`}
        />
      )}
      {expandable ? (
        <button
          type="button"
          data-testid={`${id}-expand`}
          onClick={openDialog}
          className={`self-end ${textActionClass}`}
        >
          ⤢ {t('config.edit.expandField')}
        </button>
      ) : null}
      {overMax ? (
        <p role="alert" data-testid={`${id}-over-max`} className="text-meta text-status-down">
          {t('config.editAgentic.overMax')}
        </p>
      ) : null}
      {error != null ? (
        <p role="alert" data-testid={`${id}-error`} className="text-meta text-status-down">
          {error}
        </p>
      ) : null}
      {expandable && expanded ? (
        <dialog
          ref={dialogRef}
          data-testid={`${id}-dialog`}
          onClose={() => setExpanded(false)}
          className="w-full max-w-3xl rounded-lg bg-surface-1 p-4 text-ink-1 ring-1 ring-inset ring-ink-3/20 backdrop:bg-canvas/70"
        >
          <div className="flex flex-col gap-2">
            <textarea
              ref={dialogTextareaRef}
              data-testid={`${id}-dialog-textarea`}
              rows={20}
              value={draft}
              disabled={disabled}
              onChange={(event) => setDraft(event.target.value)}
              className={`${inputClass} w-full font-mono`}
            />
            <p className="text-meta text-ink-3">
              {draft.length}/{maxLength}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="primary"
                size="sm"
                data-testid={`${id}-dialog-save`}
                onClick={() => {
                  onChange(draft);
                  dialogRef.current?.close();
                }}
              >
                {t('config.edit.dialogSave')}
              </Button>
              <Button
                size="sm"
                data-testid={`${id}-dialog-cancel`}
                onClick={() => dialogRef.current?.close()}
              >
                {t('config.edit.dialogCancel')}
              </Button>
            </div>
          </div>
        </dialog>
      ) : null}
    </div>
  );
};
